import { useState, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { FormState, FieldState } from 'formstate';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { getTermsUrl } from '@config/index';
import Modal from '@components/Modal/Modal';
import Input from '@components/Input/Input';
import Textarea from '@components/Textarea/Textarea';
import Select from '@components/Select/Select';
import Checkbox from '@components/Checkbox/Checkbox';
import Button from '@components/Button/Button';
import Heading from '@components/Heading/Heading';
import Icon from '@components/icons/Icon';
import { required, emailValid, phone, requiredConsent, postal, min, max } from '@features/userPanel/mobx/stores/validators';
import { TCountry } from '@typescript/regional';
import NextSurveyApi from '@features/api/nextApiService/nextSurveyApi';
import alertsStore from '@context/global/mobx/alertStore';
import styles from './InteriorDesingModal.styles';
import GoogleAdsGenerateLead from '@reaffiliates/GoogleAds/GoogleAdsGenerateLead';
import useBodyOverflow from '@hooks/useBodyOverflow';
type PropsType = {
  isOpen: boolean;
  onClose: () => void;
};
const InteriorDesingModal = ({
  isOpen,
  onClose
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const {
    locale,
    defaultLocale
  } = useRouter();
  const regulationsHref = getTermsUrl(locale, defaultLocale);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [consentValue, setConsentValue] = useState(false);
  useBodyOverflow(isOpen, 'interiorDesignModal', true, true);
  const validatePostalCode = async (val: string | null) => {
    const postalCode: string | undefined = val?.trim();

    // --------- validate required ------------//
    let error = required()(postalCode);
    if (error) {
      return error;
    }

    // --------- validate format ------------//
    error = postal('PL' as TCountry)(postalCode);
    if (error) {
      return error;
    }
    return error;
  };

  // Form state
  const quoteForm = useMemo(() => new FormState({
    fullName: new FieldState<string>('').validators(required()).disableAutoValidation(),
    email: new FieldState<string>('').validators(required(), emailValid()).disableAutoValidation(),
    postalCode: new FieldState<string>('').validators(validatePostalCode).disableAutoValidation(),
    phoneCountryCode: new FieldState<string>('PL'),
    phoneNumber: new FieldState<string>('').validators(required(), phone('PL' as TCountry)).disableAutoValidation(),
    comment: new FieldState<string>('').validators(min(5), max(4000)).disableAutoValidation(),
    consent: new FieldState<boolean>(false).validators(requiredConsent()).enableAutoValidation()
  }), []);

  // Phone country codes options
  const phoneCountryCodes = [{
    name: '+48',
    value: 'PL'
  }];

  // Reset success state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsSuccess(false);
    }
  }, [isOpen]);

  // Synchronize local consent state with FormState
  useEffect(() => {
    quoteForm.$.consent.onChange(consentValue);
  }, [consentValue, quoteForm]);
  const handleClose = () => {
    setIsSuccess(false);
    onClose();
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const validationResult = await quoteForm.validate();
      if (validationResult.hasError) {
        setIsSubmitting(false);
        return;
      }

      // Przygotowanie numeru telefonu
      const countryCode = quoteForm.$.phoneCountryCode.value;
      const fullPhoneNumber = `${countryCode} ${quoteForm.$.phoneNumber.value}`;

      // Wysłanie zapytania
      const response = await NextSurveyApi.submitDesignConsultation({
        fullName: quoteForm.$.fullName.value,
        postalCode: quoteForm.$.postalCode.value,
        email: quoteForm.$.email.value,
        phoneNumber: fullPhoneNumber,
        comment: quoteForm.$.comment.value || ''
      });
      if (response.error) {
        alertsStore.pushAlert({
          message: response.message ? t(response.message) : t('quoteRequest.error.submitFailed'),
          display: true
        });
        setIsSubmitting(false);
        return;
      }
      GoogleAdsGenerateLead('interior_desing');
      setIsSuccess(true);
    } catch (error) {
      console.error('Error submitting quote request:', error);
      alertsStore.pushAlert({
        message: t('quoteRequest.error.submitFailed'),
        display: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <Modal active={isOpen} closeFunc={handleClose} heading={t('interiorDesignModal.title')} closeOnOutsideClick fullScreen={true} addContentSx={styles.modalContainer(isSuccess)} addSx={styles.modalHeading} headingAddSx={styles.modalHeadingText}>
      <div sx={styles.modalContent} data-testid="interior-design-modal-content">
        {isSuccess ? (/* Success Screen */
      <>
            <div sx={styles.successContainer} data-testid="interior-design-modal-success-container">
              <div sx={styles.successIconContainer} data-testid="interior-design-modal-success-icon-container">
                <div sx={styles.successIcon} data-testid="interior-design-modal-success-icon-wrapper">
                  <Icon icon="Check" size="64px" color="darkGrey700" cy="interior-design-modal-success-icon" />
                </div>
              </div>
              <div sx={styles.textContainer} data-testid="interior-design-modal-success-text-container">
                <Heading h={5} addSx={styles.successTitle} data-testid="interior-design-modal-success-title">
                  {t('interiorDesignModal.success.title')}
                </Heading>
                <p sx={styles.successMessage} data-testid="interior-design-modal-success-message">
                  {t('interiorDesignModal.success.message')}
                </p>
              </div>
            </div>
            <div sx={styles.successFooter} data-testid="interior-design-modal-success-footer">
              <Button variant="default" variantColor="dark" onClick={handleClose} addSx={styles.closeButton} cy="interior-design-modal-success-close-button">
                {t('interiorDesignModal.success.closeButton')}
              </Button>
            </div>
          </>) : (/* Form Screen */
      <>
            {/* Scrollable Content */}
            <div sx={styles.scrollableContent} data-testid="interior-design-modal-scrollable-content">
              {/* info section */}
              <div sx={styles.infoContainer} data-testid="interior-design-modal-info-container">
                <p sx={styles.infoText} data-testid="interior-design-modal-info-text">
                  {t('interiorDesignModal.infoText1')}
                </p>
              </div>

              {/* Inquiry Content Section */}
              <div sx={styles.section} data-testid="interior-design-modal-data-section">
                <div sx={styles.sectionTitleContainer} data-testid="interior-design-modal-data-section-title-container">
                  <Heading h={4} headingType={4} addSx={styles.sectionTitle} data-testid="interior-design-modal-data-section-title">
                    {t('interiorDesignModal.yourData')}
                  </Heading>
                  <p sx={styles.formInfo} data-testid="interior-design-modal-form-info">
                    {t('interiorDesignModal.formInfo')}
                  </p>
                </div>

                <Input field={quoteForm.$.email} placeholder={t('quoteRequest.email')} addSx={styles.emailInput} cy="interior-design-modal-email-input" />
                <Input field={quoteForm.$.fullName} placeholder={t('quoteRequest.fullName')} addSx={styles.fullNameInput} cy="interior-design-modal-full-name-input" />
                <Input field={quoteForm.$.postalCode} placeholder={t('quoteRequest.postalCode')} addSx={styles.postalCodeInput} cy="interior-design-modal-postal-code-input" />
                <div sx={styles.phoneContainer} data-testid="interior-design-modal-phone-container">
                  <Select value={quoteForm.$.phoneCountryCode.value} onChange={value => quoteForm.$.phoneCountryCode.onChange(value.toString())} options={phoneCountryCodes} addSx={styles.phoneCountryCodeSelect} disabled={true} cy="interior-design-modal-phone-country-code-select" />
                  <Input field={quoteForm.$.phoneNumber} placeholder={t('quoteRequest.phoneNumber')} addSx={styles.phoneNumberInput} cy="interior-design-modal-phone-number-input" />
                </div>
              </div>

              {/* question section */}
              <div sx={styles.section} data-testid="interior-design-modal-question-section">
                <Heading h={4} headingType={4} addSx={styles.sectionTitle} data-testid="interior-design-modal-question-section-title">
                  {t('interiorDesignModal.sectionTitle')}
                </Heading>
                <Textarea field={quoteForm.$.comment} placeholder={t('interiorDesignModal.questionPlaceholder')} bottomDescription={t('interiorDesignModal.questionText')} maxLength={4000} addSx={styles.questionTextarea} cy="interior-design-modal-question-textarea" />
              </div>
            </div>

            {/* Footer - Always Visible at Bottom */}
            <div sx={styles.footer} data-testid="interior-design-modal-footer">
              <div sx={styles.consentContainer} data-testid="interior-design-modal-consent-container">
                <Checkbox value={consentValue} onChange={setConsentValue} showError={!!quoteForm.$.consent.error} error={quoteForm.$.consent.error || ''} addSx={styles.checkboxContainer} cy="interior-design-modal-consent-checkbox">
                  <span sx={styles.consentText} data-testid="interior-design-modal-consent-text">
                    {t('quoteRequest.consent')}{' '}
                    <a href={regulationsHref} target="_blank" rel="noopener noreferrer" sx={styles.regulationsLink} data-testid="interior-design-modal-regulations-link">
                      {t('quoteRequest.regulationsLink')}
                    </a>
                  </span>
                </Checkbox>
              </div>
              <Button variant="default" variantColor="brand" onClick={handleSubmit} disabled={isSubmitting} loading={isSubmitting} addSx={styles.submitButton} cy="interior-design-modal-submit-button">
                {t('interiorDesignModal.btnText')}
              </Button>
            </div>
          </>)}
      </div>
    </Modal>;
};
export default observer(InteriorDesingModal);